import { NSARouterMakerResult } from 'namirasoft-account-react';
import { NamingConvention } from 'namirasoft-core';
import { TemplateGenerator, TemplateInputRow, TemplateRow, TemplateType } from 'namirasoft-password';
import { NSBox, NSBoxBoolean, NSBoxEnum, NSBoxInteger, NSBoxString, NSBoxTextArea, NSColumn, NSRow } from 'namirasoft-site-react';
import { Component, createRef } from 'react';
import { PasswordEntities } from '../PasswordEntities';
import { PasswordLayout } from '../PasswordLayout';
import { PasswordRouterProps } from '../PasswordRouterProps';

export interface TemplateEditPageProps extends NSARouterMakerResult, PasswordRouterProps
{
    isEdit: boolean;
}

export interface TemplateEditPageState
{
    type?: TemplateType;
}

class TemplateEditPage extends Component<TemplateEditPageProps, TemplateEditPageState>
{
    NSBoxString_Name = createRef<NSBoxString>();
    NSBoxInteger_Length = createRef<NSBoxInteger>();
    NSBoxEnum_Type = createRef<NSBoxEnum<TemplateType>>();
    NSBoxBoolean_Uppercase = createRef<NSBoxBoolean>();
    NSBoxBoolean_Lowercase = createRef<NSBoxBoolean>();
    NSBoxBoolean_Digit = createRef<NSBoxBoolean>();
    NSBoxBoolean_Minus = createRef<NSBoxBoolean>();
    NSBoxBoolean_Underline = createRef<NSBoxBoolean>();
    NSBoxBoolean_Space = createRef<NSBoxBoolean>();
    NSBoxBoolean_Special = createRef<NSBoxBoolean>();
    NSBoxBoolean_Brackets = createRef<NSBoxBoolean>();
    NSBoxBoolean_Latin1 = createRef<NSBoxBoolean>();
    NSBoxString_Pattern = createRef<NSBoxString>();
    NSBoxBoolean_PatternPermute = createRef<NSBoxBoolean>();
    NSBoxString_Preview = createRef<NSBoxString>();
    NSBoxTextArea_Description = createRef<NSBoxTextArea>();

    private generate_interval: ReturnType<typeof setInterval> | null = null;

    constructor(props: TemplateEditPageProps)
    {
        super(props);
        this.state = {};

        this.getTemplateInputRow = this.getTemplateInputRow.bind(this);
        this.generate = this.generate.bind(this);
    }

    override componentDidMount(): void
    {
        this.generate_interval = setInterval(this.generate, 1000);
    }

    override componentWillUnmount(): void
    {
        if (this.generate_interval !== null)
        {
            clearInterval(this.generate_interval);
            this.generate_interval = null;
        }
    }

    getTemplateInputRow(checkError: boolean = true): TemplateInputRow
    {
        let name = this.NSBoxString_Name.current?.getValue(checkError) ?? "";
        let length = this.NSBoxInteger_Length.current?.getValue(checkError) ?? 0;
        let type = this.NSBoxEnum_Type.current?.getValueEnumOne(TemplateType, TemplateType.CharacterSet) ?? TemplateType.CharacterSet;
        let character_set_uppercase = this.NSBoxBoolean_Uppercase.current?.getValue() ?? false;
        let character_set_lowercase = this.NSBoxBoolean_Lowercase.current?.getValue() ?? false;
        let character_set_digit = this.NSBoxBoolean_Digit.current?.getValue() ?? false;
        let character_set_minus = this.NSBoxBoolean_Minus.current?.getValue() ?? false;
        let character_set_underline = this.NSBoxBoolean_Underline.current?.getValue() ?? false;
        let character_set_space = this.NSBoxBoolean_Space.current?.getValue() ?? false;
        let character_set_special = this.NSBoxBoolean_Special.current?.getValue() ?? false;
        let character_set_brackets = this.NSBoxBoolean_Brackets.current?.getValue() ?? false;
        let character_set_latin_1_supplement = this.NSBoxBoolean_Latin1.current?.getValue() ?? false;
        let pattern = this.NSBoxString_Pattern.current?.getValue(checkError) ?? null;
        let pattern_randomly_permute = this.NSBoxBoolean_PatternPermute.current?.getValue() ?? false;
        let description = this.NSBoxTextArea_Description.current?.getValue() ?? null;

        return {
            workspace_id: "",
            name,
            length,
            type,
            character_set_uppercase,
            character_set_lowercase,
            character_set_digit,
            character_set_minus,
            character_set_underline,
            character_set_space,
            character_set_special,
            character_set_brackets,
            character_set_latin_1_supplement,
            pattern,
            pattern_randomly_permute,
            description,
            template_category: [],
            template_tag: [],
            template_field: [],
        };
    }

    generate()
    {
        try
        {
            let row: TemplateRow = {
                ...this.getTemplateInputRow(false),
                id: "",
                user_id: "",
                workspace_id: "",
                created_at: new Date(),
                updated_at: new Date(),
            }

            let generated = TemplateGenerator.generate(row);
            this.NSBoxString_Preview.current?.setValue(generated)
        }
        catch (error) { }
    }

    override render()
    {
        return (
            <PasswordLayout<TemplateRow, TemplateInputRow>
                {...this.props}
                action={{
                    description: "Define a reusable rule for generating strong passwords, using a character set or a pattern, with your chosen length."
                }}
                pages={{
                    entity: PasswordEntities.template(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: () => this.getTemplateInputRow(true),
                                    setRow: (row: TemplateRow) =>
                                    {
                                        this.NSBoxString_Name.current?.setValue(row.name);
                                        this.NSBoxInteger_Length.current?.setValue(row.length);
                                        this.NSBoxEnum_Type.current?.setValueOne(row.type);
                                        this.NSBoxBoolean_Uppercase.current?.setValueBool(row.character_set_uppercase);
                                        this.NSBoxBoolean_Lowercase.current?.setValueBool(row.character_set_lowercase);
                                        this.NSBoxBoolean_Digit.current?.setValueBool(row.character_set_digit);
                                        this.NSBoxBoolean_Minus.current?.setValueBool(row.character_set_minus);
                                        this.NSBoxBoolean_Underline.current?.setValueBool(row.character_set_underline);
                                        this.NSBoxBoolean_Space.current?.setValueBool(row.character_set_space);
                                        this.NSBoxBoolean_Special.current?.setValueBool(row.character_set_special);
                                        this.NSBoxBoolean_Brackets.current?.setValueBool(row.character_set_brackets);
                                        this.NSBoxBoolean_Latin1.current?.setValueBool(row.character_set_latin_1_supplement);
                                        this.NSBoxString_Pattern.current?.setValue(row.pattern);
                                        this.NSBoxBoolean_PatternPermute.current?.setValueBool(row.pattern_randomly_permute);
                                        this.NSBoxTextArea_Description.current?.setValue(row.description);
                                    }
                                },
                            },
                        },
                    },
                }}
            >
                <NSColumn>
                    <NSBoxString
                        ref={this.NSBoxString_Name}
                        title="Name"
                        required
                        style={{ width: NSBox.width.double }}
                        info={{ link: "https://namirasoft.com/password/console-guide/template/#field-name" }}
                    />
                    <NSRow>
                        <NSBoxInteger
                            ref={this.NSBoxInteger_Length}
                            title="Length"
                            required
                            defaultValue={32}
                            info={{ link: "https://namirasoft.com/password/console-guide/template/#field-length" }}
                        />
                        <NSBoxEnum<TemplateType>
                            ref={this.NSBoxEnum_Type}
                            title="Type"
                            required
                            multiple={false}
                            defaultValue={TemplateType.CharacterSet}
                            getEnumObject={() => TemplateType}
                            getTitle={(value) => NamingConvention.auto.convert(value, NamingConvention.Pascal_Case_Space)}
                            onChanged={(box) => this.setState(() => ({ type: box.getValueOne() as TemplateType }))}
                            info={{ link: "https://namirasoft.com/password/console-guide/template/#field-type" }}
                        />
                    </NSRow>
                    {
                        this.state.type === TemplateType.CharacterSet &&
                        <>
                            <NSRow style={{ marginTop: "-0.75rem" }}>
                                <NSBoxBoolean
                                    ref={this.NSBoxBoolean_Uppercase}
                                    title="Uppercase (A, B, C, ...)"
                                    required={false}
                                    defaultValue
                                    info={{ link: "https://namirasoft.com/password/console-guide/template/#field-uppercase" }}
                                />
                                <NSBoxBoolean
                                    ref={this.NSBoxBoolean_Lowercase}
                                    title="Lowercase (a, b, c, ...)"
                                    required={false}
                                    defaultValue
                                    info={{ link: "https://namirasoft.com/password/console-guide/template/#field-lowercase" }}
                                />
                            </NSRow>
                            <NSRow style={{ marginTop: "-0.75rem" }}>
                                <NSBoxBoolean
                                    ref={this.NSBoxBoolean_Digit}
                                    title="Digit (1, 2, 3, ...)"
                                    required={false}
                                    defaultValue
                                    info={{ link: "https://namirasoft.com/password/console-guide/template/#field-digit" }}
                                />
                                <NSBoxBoolean
                                    ref={this.NSBoxBoolean_Space}
                                    title="Space ( )"
                                    required={false}
                                    info={{ link: "https://namirasoft.com/password/console-guide/template/#field-space" }}
                                />
                            </NSRow>
                            <NSRow style={{ marginTop: "-0.75rem" }}>
                                <NSBoxBoolean
                                    ref={this.NSBoxBoolean_Underline}
                                    title="Underline (_)"
                                    required={false}
                                    info={{ link: "https://namirasoft.com/password/console-guide/template/#field-underline" }}
                                />
                                <NSBoxBoolean
                                    ref={this.NSBoxBoolean_Minus}
                                    title="Minus (-)"
                                    required={false}
                                    info={{ link: "https://namirasoft.com/password/console-guide/template/#field-minus" }}
                                />
                            </NSRow>
                            <NSRow style={{ marginTop: "-0.75rem" }}>
                                <NSBoxBoolean
                                    ref={this.NSBoxBoolean_Special}
                                    title="Special (!, @, #, $, ...)"
                                    required={false}
                                    info={{ link: "https://namirasoft.com/password/console-guide/template/#field-special" }}
                                />
                                <NSBoxBoolean
                                    ref={this.NSBoxBoolean_Brackets}
                                    title="Brackets ([, ], {, }, (, ), <, >)"
                                    required={false}
                                    info={{ link: "https://namirasoft.com/password/console-guide/template/#field-brackets" }}
                                />
                            </NSRow>
                            <NSBoxBoolean
                                ref={this.NSBoxBoolean_Latin1}
                                title="Latin-1 Supplement (À, É, Ô, Ü, ...)"
                                required={false}
                                style={{ width: NSBox.width.double, marginTop: "-0.75rem" }}
                                info={{ link: "https://namirasoft.com/password/console-guide/template/#field-latin" }}
                            />
                        </>
                    }
                    {
                        this.state.type === TemplateType.Pattern &&
                        <>
                            < NSBoxString
                                ref={this.NSBoxString_Pattern}
                                title="Pattern"
                                required
                                style={{ width: NSBox.width.double }}
                                info={{ link: "https://namirasoft.com/password/console-guide/template/#field-pattern" }}
                            />
                            <NSBoxBoolean
                                ref={this.NSBoxBoolean_PatternPermute}
                                title="Randomly Permute Pattern"
                                required={false}
                                style={{ marginTop: "-0.75rem" }}
                                info={{ link: "https://namirasoft.com/password/console-guide/template/#field-randomly-permute-pattern" }}
                            />
                        </>
                    }
                    <NSBoxString
                        ref={this.NSBoxString_Preview}
                        title="Preview"
                        required={false}
                        style={{ width: NSBox.width.double }}
                        menu={{
                            items: [],
                            builtin: {
                                copy: {
                                    enabled: true,
                                    getValue: () => this.NSBoxString_Preview.current?.getValue() ?? ""
                                }
                            },
                        }}
                        info={{ link: "https://namirasoft.com/password/console-guide/template/#field-preview" }}
                    />
                    <NSBoxTextArea
                        ref={this.NSBoxTextArea_Description}
                        title="Description"
                        required={false}
                        style={{ width: NSBox.width.double }}
                        info={{ link: "https://namirasoft.com/password/console-guide/template/#field-description" }}
                    />
                </NSColumn>
            </PasswordLayout>
        )
    }
}

export default TemplateEditPage;